import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Divider, Button, TextField } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text } from "../elements";
import { formatAmountSuffix } from "../../utils/formatters";

// 1. Static Theme Palettes
const DARK_COLORS = {
  bg: "#1b2b4d",
  innerCardBg: "rgba(255, 255, 255, 0.03)",
  border: "#2f3f61",
  hoverBorder: "#39496b",
  title: "#f0f0f0",
  text: "#94a3b8",
  green: "#10b981",
  red: "#ef4444",
  blue: "#3b82f6",
};

const LIGHT_COLORS = {
  bg: "#f8fafc",
  innerCardBg: "#f1f5f9",
  border: "#e2e8f0",
  hoverBorder: "#cbd5e1",
  title: "#1e293b",
  text: "#64748b",
  green: "#059669",
  red: "#dc2626",
  blue: "#2563eb",
};

// 2. Defensive API & State Interfaces
export interface DailyReportTransaction {
  id?: string | number;
  _id?: string | number;
  type?: string;
  transactionType?: string;
  isIncome?: boolean;
  isExpense?: boolean;
  amount?: number | string;
  totalAmount?: number | string;
  createdAt?: string;
  date?: string;
  categoryName?: string;
  title?: string;
  project?: {
    name?: string;
  };
  projectName?: string;
}

export interface DailyReportApiResponse {
  rows?: DailyReportTransaction[];
  count?: number;
  totalIncome?: number | string;
  totalExpense?: number | string;
}

interface TransactionRow {
  id: string;
  time: string;
  type: "income" | "expense";
  title: string;
  projectName: string;
  amount: number;
}

interface DailySummaryState {
  income: number;
  expense: number;
  transactionsCount: number;
  rows: TransactionRow[];
}

interface DailyTransactionsCardProps {
  onViewReport?: (selectedDate: string) => void;
}

const parseAmount = (value: unknown): number => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.abs(amount) : 0;
};

export const DailyTransactionsCard: React.FC<DailyTransactionsCardProps> = ({ onViewReport }) => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const todayStr = useMemo(() => moment().format("YYYY-MM-DD"), []);
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [summaryData, setSummaryData] = useState<DailySummaryState>({
    income: 0,
    expense: 0,
    transactionsCount: 0,
    rows: [],
  });

  const latestRequestId = useRef<number>(0);

  const isToday = selectedDate === todayStr;
  const colors = theme !== "light_mode" ? DARK_COLORS : LIGHT_COLORS;

  const fetchDateData = useCallback(async () => {
    if (!selectedDate) return;

    const currentRequestId = ++latestRequestId.current;

    try {
      setLoading(true);
      setError(false);

      const fromDate = moment(selectedDate, "YYYY-MM-DD").startOf("day").toISOString();
      const toDate = moment(selectedDate, "YYYY-MM-DD").endOf("day").toISOString();

      const PAGE_SIZE = 100;
      const CONCURRENCY_CHUNK_SIZE = 5;

      const baseParams = {
        fromDate,
        toDate,
        limit: PAGE_SIZE,
      };

      const firstResponse = await AnalyticsService.getDailyReport({
        ...baseParams,
        page: 1,
      });

      if (currentRequestId !== latestRequestId.current) return;

      const firstPageData: DailyReportApiResponse = firstResponse?.data || {};
      const allRows: DailyReportTransaction[] = [...(firstPageData.rows || [])];
      const totalCount = Number(firstPageData.count || 0);

      if (totalCount > PAGE_SIZE) {
        const totalPages = Math.ceil(totalCount / PAGE_SIZE);
        const remainingPageNumbers = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);

        for (let i = 0; i < remainingPageNumbers.length; i += CONCURRENCY_CHUNK_SIZE) {
          if (currentRequestId !== latestRequestId.current) return;

          const batch = remainingPageNumbers.slice(i, i + CONCURRENCY_CHUNK_SIZE);
          const batchResponses = await Promise.all(
            batch.map((page) =>
              AnalyticsService.getDailyReport({
                ...baseParams,
                page,
              }),
            ),
          );

          if (currentRequestId !== latestRequestId.current) return;

          batchResponses.forEach((res) => {
            const pageRows: DailyReportTransaction[] = res?.data?.rows || [];
            allRows.push(...pageRows);
          });
        }
      }

      let calculatedTotalIncome = 0;
      let calculatedTotalExpense = 0;
      const formattedRows: TransactionRow[] = [];

      allRows.forEach((item, index) => {
        const amt = parseAmount(item.amount ?? item.totalAmount);
        if (amt <= 0) return;

        const typeStr = (item.type || item.transactionType || "").toLowerCase();

        let transactionType: "income" | "expense" | null = null;
        if (typeStr === "income" || item.isIncome === true) {
          transactionType = "income";
        } else if (typeStr === "expense" || item.isExpense === true || item.isIncome === false) {
          transactionType = "expense";
        }

        if (!transactionType) return;

        if (transactionType === "income") {
          calculatedTotalIncome += amt;
        } else {
          calculatedTotalExpense += amt;
        }

        const rawDate = item.createdAt ?? item.date;
        const timeFormatted = rawDate ? moment(rawDate).format("HH:mm") : "--:--";

        const baseId = item.id ?? item._id ?? `tx-${selectedDate}-${index}`;

        formattedRows.push({
          id: `${transactionType}-${baseId}`,
          time: timeFormatted,
          type: transactionType,
          title: item.categoryName || item.title || (transactionType === "income" ? "Client Income" : "Expense"),
          projectName: item.project?.name || item.projectName || "General",
          amount: amt,
        });
      });

      const hasBackendIncome = firstPageData.totalIncome !== undefined && firstPageData.totalIncome !== null;
      const hasBackendExpense = firstPageData.totalExpense !== undefined && firstPageData.totalExpense !== null;

      setSummaryData({
        income: hasBackendIncome ? parseAmount(firstPageData.totalIncome) : calculatedTotalIncome,
        expense: hasBackendExpense ? parseAmount(firstPageData.totalExpense) : calculatedTotalExpense,
        transactionsCount: formattedRows.length,
        rows: formattedRows,
      });
    } catch (err: unknown) {
      if (currentRequestId === latestRequestId.current) {
        console.error("🚀 ~ fetchDateData error:", err);
        setError(true);
      }
    } finally {
      if (currentRequestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchDateData();
  }, [fetchDateData]);

  const handleNavigate = () => {
    if (onViewReport) {
      onViewReport(selectedDate);
    } else {
      navigate(`/reports/daily?date=${selectedDate}`);
    }
  };

  const hasData = !error && summaryData.rows.length > 0;

  return (
    <Box
      className="mc-card"
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "16px",
        p: 2.5,
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: theme === "light_mode" ? "0px 4px 16px rgba(0, 0, 0, 0.04)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      {/* 1. Header Area with Date Picker */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.5,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: theme !== "light_mode" ? "#22355c" : `${colors.blue}1F`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ReceiptLongRoundedIcon
              sx={{
                color: colors.blue,
                fontSize: "22px",
              }}
            />
          </Box>

          <Box>
            <Heading
              as="h3"
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: colors.title,
                lineHeight: 1.2,
              }}
            >
              Daily Transactions
            </Heading>
            <Text
              style={{
                fontSize: "12px",
                color: colors.text,
                marginTop: "4px",
              }}
            >
              {isToday
                ? "Today's financial activity"
                : `Activity for ${moment(selectedDate, "YYYY-MM-DD").format("DD MMM YYYY")}`}
            </Text>
          </Box>
        </Box>

        <TextField
          type="date"
          size="medium"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          inputProps={{
            max: todayStr,
          }}
          sx={{
            width: "175px",
            "& .MuiOutlinedInput-root": {
              height: "42px",
              borderRadius: "8px",
              backgroundColor: colors.innerCardBg,
              fontSize: "14px",
              fontWeight: 600,
              color: colors.title,
              "& fieldset": {
                borderColor: colors.border,
              },
              "&:hover fieldset": {
                borderColor: colors.hoverBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.blue,
              },
            },
            "& input::-webkit-calendar-picker-indicator": {
              filter: theme !== "light_mode" ? "invert(0.8)" : "none",
              cursor: "pointer",
            },
          }}
        />
      </Box>

      {/* 2. Content / Loading / Empty State Area */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            py: 8,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      ) : hasData ? (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, mt: 2 }}>
          {/* Top 3 Metric Blocks */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {/* Income */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: colors.innerCardBg,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Text
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: colors.text,
                  textTransform: "uppercase",
                }}
              >
                Income
              </Text>
              <Heading
                as="h4"
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: colors.green,
                  marginTop: "4px",
                }}
              >
                {formatAmountSuffix(summaryData.income, true)}
              </Heading>
            </Box>

            {/* Expense */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: colors.innerCardBg,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Text
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: colors.text,
                  textTransform: "uppercase",
                }}
              >
                Expense
              </Text>
              <Heading
                as="h4"
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: colors.red,
                  marginTop: "4px",
                }}
              >
                {formatAmountSuffix(summaryData.expense, true)}
              </Heading>
            </Box>

            {/* Transactions Count */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: colors.innerCardBg,
                border: `1px solid ${colors.border}`,
                textAlign: "center",
                gridColumn: { xs: "span 2", sm: "span 1" },
              }}
            >
              <Text
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: colors.text,
                  textTransform: "uppercase",
                }}
              >
                Transactions
              </Text>
              <Heading
                as="h4"
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: colors.title,
                  marginTop: "4px",
                }}
              >
                {summaryData.transactionsCount}
              </Heading>
            </Box>
          </Box>

          <Divider sx={{ borderColor: colors.border, mb: 1.5 }} />

          {/* 3. Scrollable List of All Validated Transactions */}
          <Box
            sx={{
              flex: 1,
              maxHeight: "260px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "5px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: colors.hoverBorder,
                borderRadius: "4px",
              },
            }}
          >
            {summaryData.rows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "8px 12px",
                  borderRadius: "8px",
                  backgroundColor: colors.innerCardBg,
                  border: `1px solid ${colors.border}`,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: colors.hoverBorder,
                  },
                }}
              >
                {/* Left: Time & Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    minWidth: "140px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: colors.text,
                      minWidth: "35px",
                    }}
                  >
                    {row.time}
                  </Text>
                  <Text
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: colors.title,
                      maxWidth: "120px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {row.title}
                  </Text>
                </Box>

                <Text
                  style={{
                    fontSize: "12px",
                    color: colors.text,
                    flex: 1,
                    minWidth: 0,
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                  }}
                >
                  {row.projectName}
                </Text>

                {/* Right: Formatted Signed Amount */}
                <Text
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: row.type === "income" ? colors.green : colors.red,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.type === "income"
                    ? `+${formatAmountSuffix(row.amount, true)}`
                    : `-${formatAmountSuffix(row.amount, true)}`}
                </Text>
              </Box>
            ))}
          </Box>

          {/* 4. Bottom Footer Link */}
          <Box sx={{ mt: 1.5, textAlign: "center" }}>
            <Button
              onClick={handleNavigate}
              endIcon={<ChevronRightRoundedIcon />}
              sx={{
                color: colors.blue,
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
              }}
            >
              View Daily Report
            </Button>
          </Box>
        </Box>
      ) : (
        /* Empty State */
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ color: colors.text }}>No transaction data available</Text>
        </Box>
      )}
    </Box>
  );
};

export default DailyTransactionsCard;
